import {ReactNode, RefObject, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useOpenModals} from 'context/openModals';
import {usePath} from '../../hooks';
import {SessionStorage} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {PREVIOUS_PAGE_STORAGE_KEY} from 'utils/constants';
import Navbar from 'components/common/Navbar';
import Loading from 'components/common/Loading';
import SubHeaderPanel from '../SubHeaderPanel';
// import LanguageSDKSwitcher from '../LanguageSDKSwitcher';
import SubHeader, {SubHeaderProps} from 'components/common/SubHeader';
import {HeightObserverProvider} from 'context/heightObserver';
import {cn, ScrollArea} from '@chekin/guestapp-ui-kit';
import {PageWrapper, PageBanner, Wrapper, BottomStickyPanel} from './styled';

type BannerProps = {
  src: string;
  onClick?: () => void;
};
export type PageProps = SubHeaderProps & {
  children: ReactNode;
  isSubHeaderVisible?: boolean;
  subtitle?: string;
  className?: string;
  pageWrapperClassName?: string;
  isLoading?: boolean;
  mainHeader?: boolean;
  hideSubPanelBackButton?: boolean;
  onBackButtonSubHeaderClick?: () => void;
  headerLogo?: string;
  banner?: BannerProps;
  navBarTitle?: string;
  bottomStickyPanel?: ReactNode;
  bottomStickyPanelRef?: RefObject<HTMLDivElement>;
  subheaderRightAddon?: ReactNode;
};

function Page({
  children = '',
  title = '',
  subtitle,
  isSubHeaderVisible = false,
  className,
  pageWrapperClassName,
  isLoading = false,
  mainHeader,
  isBackButtonDisabled = false,
  hideMenu = false,
  onBackButtonClick,
  hideSubPanelBackButton = false,
  onBackButtonSubHeaderClick,
  subheaderRightAddon,
  headerLogo,
  banner,
  navBarTitle,
  bottomStickyPanel,
  bottomStickyPanelRef,
}: PageProps) {
  const stickyPanelRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState(0);

  useEffect(() => {
    const el = stickyPanelRef.current;
    if (!el) return;

    const observer = new ResizeObserver(entries => {
      setSpacerHeight(entries[0].contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const {checkIfOpenDiscardModalAndRedirect} = useOpenModals();
  const navigate = useNavigate();
  const {getMainPath} = usePath();
  const handleBackButtonClick =
    onBackButtonSubHeaderClick || (() => checkIfOpenDiscardModalAndRedirect('-1'));
  const handleSubHeaderBackButtonClick =
    onBackButtonClick ??
    (() => {
      if (!SessionStorage.get(PREVIOUS_PAGE_STORAGE_KEY)) {
        navigate(getMainPath(PATHS.home));
      } else {
        navigate(-1);
      }
    });

  const hasBottomPanel = bottomStickyPanel != null || bottomStickyPanelRef != null;

  return (
    <Loading isLoading={isLoading}>
      <PageWrapper className={cn('page', pageWrapperClassName)}>
        <ScrollArea type="always">
          <HeightObserverProvider>
            <Navbar visible={mainHeader} title={navBarTitle} />
            {isSubHeaderVisible && (
              <SubHeader
                title={title}
                isBackButtonDisabled={isBackButtonDisabled}
                hideMenu={hideMenu}
                onBackButtonClick={handleSubHeaderBackButtonClick}
                headerLogo={headerLogo}
                mobileTitle={navBarTitle}
                rightAddon={subheaderRightAddon}
              />
            )}
            {subtitle && (
              <SubHeaderPanel
                title={subtitle}
                onBackClick={handleBackButtonClick}
                hideBackButton={hideSubPanelBackButton}
              />
            )}
            {banner && (
              <PageBanner
                src={banner?.src}
                onClick={() => banner.onClick?.()}
                className="page-banner"
              />
            )}
            <Wrapper
              className={cn(
                'page_content',
                'kiosko:px-[94px] kiosko:pt-[170px] kiosko:gap-14',
                className,
              )}
            >
              {children}
            </Wrapper>
            {hasBottomPanel && (
              <>
                <div style={{height: spacerHeight}} aria-hidden="true" />
                <BottomStickyPanel ref={stickyPanelRef} id={'bottomStickyPanel'}>
                  <div ref={bottomStickyPanelRef}>{bottomStickyPanel}</div>
                </BottomStickyPanel>
              </>
            )}
            {/*<LanguageSDKSwitcher />*/}
          </HeightObserverProvider>
        </ScrollArea>
      </PageWrapper>
    </Loading>
  );
}

export {Page};
